import { CartFund, UnitCategoryCode } from 'cart-model';
import { EventType } from 'common-event';
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { formatCurrency, Card, CurrencyInput, Icon, Title, FundListItemIcons, FieldHelperText, toHashColorHexString, FieldLabel, Typography } from 'ui-components';
import MultipleWordsTextWithArrow from 'ui-components/src/MultipleWordsTextWithArrow/MultipleWordsTextWithArrow';
import { ReactComponent as DeleteIcon } from 'common-assets/assets/icons/close_icon.svg';
import { logChangeCardFundAmount, logFundEvent, logRemoveFromCart } from '../../../model/events';
import { productWizardActions } from '../../../model/store';
import FundDetailsButtonWithDialog from '../../FundDetailsButtonWithDialog/FundDetailsButtonWithDialog';
import Screen from '../../ProductWizard/Screen';
import { AmountValidationError as AmountValidationErrorType, AmountValidationErrorType as ValidationErrorType } from '../cartValidation';

import useStyles from './SetAmountsFundBox.style';

const maxAllowedCartItemValue = 999999999.99;

const SetAmountsFundBox: FunctionComponent<{
  cartFund: CartFund;
  unitCategory: UnitCategoryCode;
  minAmount: number;
  validationError: AmountValidationErrorType;
  index: number;
  sumError?: boolean;
  onInputFocus?: () => void;
}> = ({cartFund, unitCategory, minAmount, validationError, index, sumError, onInputFocus}) => {

  const c = useStyles({});
  const uniqueIdentifier = `fund-${cartFund.fund.id}-${unitCategory}`.toLowerCase();
  const [t] = useTranslation('set-amounts-per-fund');
  const dispatch = useDispatch();

  const hasValidationError = validationError.type !== ValidationErrorType.NONE;
  const showFieldErrorMessage = hasValidationError && !sumError;
  const validationErrorMessage = t(`amount-validation-${validationError.type}-${validationError.productType.toLowerCase()}`, {
    productType: validationError.productType.toUpperCase(),
    min: formatCurrency(validationError.min || 0),
    max: formatCurrency(validationError.max || 0),
    year: new Date().getFullYear()
  });

  const removeCartFund = () => dispatch(
      productWizardActions.removeCartFund({ fund: cartFund.fund, productType: cartFund.productType }));

  const setCardFundAmount = (value?: number) => {
    dispatch(productWizardActions.setCartFundAmount({
        fund: cartFund.fund,
        productType: cartFund.productType,
        amount: value || 0 }))};
  const titleNode = (
    <MultipleWordsTextWithArrow
      text={cartFund.fund.name}
      classNameWrapper={c.textWrapper}
      classNameText={c.text}
      classNameIcon={c.icon}
    />
  );

  return (
    <div className={c.root}>
      <Card className={c.sumFundItem} hoverable>
        <div className={c.sumFindItemText}>
          <FundListItemIcons
            fund={cartFund.fund}
            isFundInadequate={false}
            umbrellaColor={toHashColorHexString(cartFund.fund?.umbrella?.colorHex)}
          />
          <FundDetailsButtonWithDialog
            fund={cartFund.fund}
            productType={cartFund.productType}
            buttonLabel={t('more-information')}
            source={Screen.SET_AMOUNTS_PER_FUND}
            index={index}
            customButton={
              <Title
                title={titleNode}
                titleComponent="h4"
                titleVariant="h4"
                titleFontWeight="bold"
              />
            }
            className={c.button}
            titleAsButton
          />
          {cartFund.fund.previousName && <Typography variant="body2" textColor="greys.60">{cartFund.fund.previousName}</Typography>}
        </div>
        <div className={c.sumFindItemValue}>
          <div className={c.formControl}>
            <FieldLabel htmlFor={uniqueIdentifier} className={c.inputLabel}>
              {t('your-amount')}
            </FieldLabel>
            <CurrencyInput
              value={cartFund.amount}
              onValueChange={setCardFundAmount}
              onFocus={event => {
                onInputFocus && onInputFocus();
                // @ts-ignore
                event.target.oldValue = cartFund.amount;
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                // @ts-ignore
                if (cartFund.amount !== event.target.oldValue) {
                  // @ts-ignore
                  logChangeCardFundAmount(cartFund, event.target.oldValue);
                }
              }}
              isAllowed={values => (values.floatValue || 0) <= maxAllowedCartItemValue}
              id={uniqueIdentifier}
              required
              hasError={hasValidationError || !!sumError}
              error={showFieldErrorMessage ? validationErrorMessage : undefined}
              className={sumError ? c.sumError : undefined}
            />
            {!showFieldErrorMessage && <FieldHelperText id={`${uniqueIdentifier}-helper-text`}>{t('min-amount', { amount: minAmount })}</FieldHelperText>}
          </div>
          <button
            type="button"
            tabIndex={0}
            onClick={() => {
              removeCartFund();
              logFundEvent(EventType.productRemove, Screen.SET_AMOUNTS_PER_FUND, cartFund.fund, cartFund.productType);
              logRemoveFromCart(cartFund.fund, cartFund.productType, Screen.SET_AMOUNTS_PER_FUND, cartFund.amount);
            }}
            className={c.deleteButton}
            aria-label={t('remove-aria-label', { itemName: cartFund.fund.name })}
          >
            <Icon>
              <DeleteIcon />
            </Icon>
          </button>
        </div>
      </Card>
    </div>
  );
};

export default SetAmountsFundBox;
